import React, { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import toast from 'react-hot-toast';

import {
  BdFraudService,
  BdFraudEnforcementMode,
  BdReviewLog,
  BdReviewOutcome,
  BdReviewResolution,
  BdReviewTier,
} from 'us-web-services';
import { colors } from 'us-react-components/dist/constants/Styles';
import PageStyles from 'src/styles/PageStyles';

import { BD_ENFORCEMENT_OPTIONS, BD_RESOLUTION_LABELS } from './constants';
import BdReviewRow from './components/BdReviewRow';

function BdFraudReview() {
  const { classes } = PageStyles();
  const [items, setItems] = useState<BdReviewLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [tierFilter, setTierFilter] = useState<BdReviewTier | ''>('');
  const [outcomeFilter, setOutcomeFilter] = useState<BdReviewOutcome | ''>('');
  const [reviewedFilter, setReviewedFilter] = useState<'true' | 'false' | ''>('false');
  const [loading, setLoading] = useState(false);
  const [enforcementMode, setEnforcementMode] = useState<BdFraudEnforcementMode>('OFF');
  const [enforcementLoading, setEnforcementLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<BdFraudEnforcementMode | null>(null);
  const [scanDialogOpen, setScanDialogOpen] = useState(false);
  const [scanStartDate, setScanStartDate] = useState('');
  const [scanEndDate, setScanEndDate] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await BdFraudService.list({
        tier: tierFilter === '' ? undefined : tierFilter,
        outcome: outcomeFilter === '' ? undefined : outcomeFilter,
        reviewed: reviewedFilter === '' ? undefined : reviewedFilter === 'true',
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      });

      setItems(response.data.data);
      setTotal(response.data.meta.size);
    } catch (error) {
      console.warn('BD fraud fetch failed:', error);
      toast.error('Failed to load BD fraud data');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, tierFilter, outcomeFilter, reviewedFilter]);

  const fetchEnforcementMode = useCallback(async () => {
    try {
      const response = await BdFraudService.getEnforcementMode();

      setEnforcementMode(response.data.data.mode);
    } catch (error) {
      console.warn('BD enforcement-mode fetch failed:', error);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);
  useEffect(() => {
    fetchEnforcementMode();
  }, [fetchEnforcementMode]);

  const handleEnforcementConfirm = async () => {
    const newMode = confirmDialog;

    setConfirmDialog(null);
    if (!newMode) return;

    setEnforcementLoading(true);
    try {
      const response = await BdFraudService.setEnforcementMode(newMode);

      setEnforcementMode(response.data.data.mode);
      const label = BD_ENFORCEMENT_OPTIONS.find((option) => option.value === newMode)?.label ?? newMode;

      toast.success(`Enforcement mode set to: ${label}`);
    } catch (error) {
      console.warn('BD enforcement-mode update failed:', error);
      toast.error('Failed to update enforcement mode');
    } finally {
      setEnforcementLoading(false);
    }
  };

  const handleResolve = async (
    id: number,
    resolution: BdReviewResolution,
    notes?: string,
  ) => {
    try {
      await BdFraudService.resolve(id, resolution, notes);
      const label = BD_RESOLUTION_LABELS[resolution] ?? resolution;

      toast.success(`Marked as ${label}`);
      fetchData();
    } catch (error) {
      console.warn('BD review resolve failed:', error);
      toast.error('Failed to resolve review');
    }
  };

  const handleTriggerScan = async () => {
    if (!scanStartDate || !scanEndDate) {
      toast.error('Both start and end dates are required');

      return;
    }
    setScanDialogOpen(false);
    try {
      toast('Retroactive scan running...');
      const response = await BdFraudService.runScan(scanStartDate, scanEndDate);
      const result = response.data.data;

      toast.success(
        `Scan complete: ${result.bookingsScanned} scanned, ${result.rowsRecorded} flagged`,
      );
      fetchData();
    } catch (error) {
      console.warn('BD retroactive scan failed:', error);
      toast.error('Scan failed');
    }
  };

  const pendingOption = BD_ENFORCEMENT_OPTIONS.find((option) => option.value === confirmDialog);

  return (
    <Paper className={classes.paper}>
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant='h5'>BD Self-Payout Fraud</Typography>
          <Button variant='outlined' size='small' onClick={() => setScanDialogOpen(true)}>
            Retroactive Scan
          </Button>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <FormControl size='small' sx={{ minWidth: 200 }}>
            <InputLabel>Enforcement Mode</InputLabel>
            <Select
              value={enforcementMode}
              label='Enforcement Mode'
              disabled={enforcementLoading}
              onChange={(e) => {
                const next = e.target.value as BdFraudEnforcementMode;

                if (next !== enforcementMode) setConfirmDialog(next);
              }}
            >
              {BD_ENFORCEMENT_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant='body2' sx={{ color: colors.gray[500] }}>
            {BD_ENFORCEMENT_OPTIONS.find((option) => option.value === enforcementMode)?.description}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
          <FormControl size='small' sx={{ minWidth: 150 }}>
            <InputLabel>Tier</InputLabel>
            <Select
              value={tierFilter}
              label='Tier'
              onChange={(event) => {
                setTierFilter(event.target.value as BdReviewTier | '');
                setPage(0);
              }}
            >
              <MenuItem value=''>All</MenuItem>
              <MenuItem value='CONFIRMED'>Confirmed</MenuItem>
              <MenuItem value='MEDIUM'>Medium</MenuItem>
              <MenuItem value='LOW'>Low</MenuItem>
            </Select>
          </FormControl>

          <FormControl size='small' sx={{ minWidth: 180 }}>
            <InputLabel>Outcome</InputLabel>
            <Select
              value={outcomeFilter}
              label='Outcome'
              onChange={(event) => {
                setOutcomeFilter(event.target.value as BdReviewOutcome | '');
                setPage(0);
              }}
            >
              <MenuItem value=''>All</MenuItem>
              <MenuItem value='OBSERVED'>Observed</MenuItem>
              <MenuItem value='HELD_FOR_REVIEW'>Held for Review</MenuItem>
              <MenuItem value='BLOCKED'>Blocked</MenuItem>
              <MenuItem value='ALLOWED'>Allowed</MenuItem>
              <MenuItem value='RETROACTIVE_SCAN'>Retroactive Scan</MenuItem>
            </Select>
          </FormControl>

          <FormControl size='small' sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={reviewedFilter}
              label='Status'
              onChange={(event) => {
                setReviewedFilter(event.target.value as 'true' | 'false' | '');
                setPage(0);
              }}
            >
              <MenuItem value=''>All</MenuItem>
              <MenuItem value='false'>Pending Review</MenuItem>
              <MenuItem value='true'>Resolved</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <TableContainer>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40 }} />
                <TableCell sx={{ width: 130 }}>Tier</TableCell>
                <TableCell sx={{ width: 90 }}>Booking</TableCell>
                <TableCell sx={{ width: 80 }}>Payor</TableCell>
                <TableCell sx={{ width: 80 }}>Payee</TableCell>
                <TableCell sx={{ width: 160 }}>Company</TableCell>
                <TableCell>Signals</TableCell>
                <TableCell sx={{ width: 130 }}>Outcome</TableCell>
                <TableCell sx={{ width: 180, whiteSpace: 'nowrap' }}>Created</TableCell>
                <TableCell sx={{ width: 240 }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={10} align='center'>Loading...</TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={10} align='center'>No results found</TableCell></TableRow>
              ) : (
                items.map((item) => (
                  <BdReviewRow key={item.id} item={item} onResolve={handleResolve} />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component='div'
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Box>

      <Dialog open={!!confirmDialog} onClose={() => setConfirmDialog(null)}>
        <DialogTitle>Change Enforcement Mode</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Change enforcement mode to <strong>{pendingOption?.label}</strong>?
          </DialogContentText>
          <DialogContentText sx={{ mt: 1 }}>{pendingOption?.description}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(null)} disabled={enforcementLoading}>Cancel</Button>
          <Button onClick={handleEnforcementConfirm} variant='contained' disabled={enforcementLoading}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={scanDialogOpen} onClose={() => setScanDialogOpen(false)}>
        <DialogTitle>Retroactive Scan</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Scan BD bookings created within a date range. Each match writes a log row with
            outcome <code>RETROACTIVE_SCAN</code>; previously-scanned (booking, payor, payee)
            tuples are skipped via scan-hash dedup.
          </DialogContentText>
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <TextField
              label='Start Date'
              type='date'
              size='small'
              value={scanStartDate}
              onChange={(e) => setScanStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label='End Date'
              type='date'
              size='small'
              value={scanEndDate}
              onChange={(e) => setScanEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScanDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleTriggerScan} variant='contained'>Start Scan</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

export default BdFraudReview;
